import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Check, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { OnboardingHeader } from "@/components/patterns/headers";
import { LoadingButton } from "@/components/ui/custom/loading-button";
import { fieldMessageVariants } from "@/components/ui/field-styles";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { useDebounce } from "@/hooks/use-debounce";
import { useSession } from "@/hooks/use-session";
import { trpcClient, useTRPC } from "@/lib/trpc";

export const Route = createFileRoute("/onboarding/brand/new")({
      component: RouteComponent,
});

type DomainValidationStatus = "idle" | "validating" | "valid" | "invalid";

const extractDomain = (input: string): string => {
      let value = input.trim().toLowerCase();
      value = value.replace(/^https?:\/\//, "");
      value = value.replace(/\/.*$/, "");
      value = value.replace(/:\d+$/, "");
      return value;
};

const isValidDomainFormat = (domain: string): boolean => {
      const domainRegex = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/;
      return domainRegex.test(domain);
};

const normalizeWebsiteUrl = (input: string): string => {
      const trimmed = input.trim();
      const hasProtocol = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed);
      const candidateUrl = hasProtocol ? trimmed : `https://${trimmed}`;

      try {
            const parsed = new URL(candidateUrl);
            return `${parsed.protocol}//${parsed.host}`;
      } catch {
            const domain = extractDomain(input);
            return `https://${domain}`;
      }
};

function RouteComponent() {
      const trpc = useTRPC();
      const queryClient = useQueryClient();
      const navigate = useNavigate();
      const { sessionData, isLoading: isLoadingSession } = useSession();

      const [name, setName] = useState("");
      const [website, setWebsite] = useState("");
      const [isSubmitting, setIsSubmitting] = useState(false);
      const [domainValidationStatus, setDomainValidationStatus] = useState<DomainValidationStatus>("idle");
      const debouncedWebsite = useDebounce(website, 500);
      const validationRequestIdRef = useRef(0);

      const createBrandMutation = useMutation(
            trpc.brands.createBrand.mutationOptions({
                  onSuccess: () => {
                        queryClient.invalidateQueries({
                              queryKey: trpc.brands.getOrganizationBrands.queryKey({}),
                        });
                  },
            }),
      );

      const populateBrandMutation = useMutation(
            trpc.brands.populateBrand.mutationOptions({
                  onSuccess: () => {
                        queryClient.invalidateQueries({
                              queryKey: trpc.brands.getOrganizationBrands.queryKey({}),
                        });
                  },
            }),
      );

      useEffect(() => {
            if (!sessionData && !isLoadingSession) {
                  navigate({ to: "/auth", replace: true });
            }
      }, [sessionData, isLoadingSession, navigate]);

      useEffect(() => {
            if (!debouncedWebsite.trim()) {
                  setDomainValidationStatus("idle");
                  return;
            }

            const domain = extractDomain(debouncedWebsite);
            if (!isValidDomainFormat(domain)) {
                  setDomainValidationStatus("invalid");
                  return;
            }

            const requestId = ++validationRequestIdRef.current;

            setDomainValidationStatus("validating");

            void (async () => {
                  try {
                        const result = await trpcClient.brands.validateWebsite.query({
                              website: debouncedWebsite,
                        });

                        if (requestId !== validationRequestIdRef.current) return;
                        setDomainValidationStatus(result.isReachable ? "valid" : "invalid");
                  } catch {
                        if (requestId !== validationRequestIdRef.current) return;
                        setDomainValidationStatus("invalid");
                  }
            })();

            return () => {
                  validationRequestIdRef.current += 1;
            };
      }, [debouncedWebsite]);

      const handleSubmit = async (e: React.FormEvent) => {
            e.preventDefault();

            if (!name.trim() || !website.trim()) {
                  toast.error("Please fill in all required fields");
                  return;
            }

            const domain = extractDomain(website);
            if (!isValidDomainFormat(domain)) {
                  toast.error("Please enter a valid website URL");
                  return;
            }

            setIsSubmitting(true);
            const normalizedUrl = normalizeWebsiteUrl(website);

            try {
                  const newBrand = await createBrandMutation.mutateAsync({
                        name: name.trim(),
                        website: normalizedUrl,
                        industry: "Other",
                        description: "",
                        products: [],
                        logo: null,
                  });

                  await populateBrandMutation.mutateAsync({ id: newBrand.id });

                  navigate({
                        to: "/onboarding/brand/$brandId/profile",
                        params: { brandId: newBrand.id },
                  });
            } catch (error) {
                  console.error("Failed onboarding step 1:", error);
                  toast.error("Failed to create your brand. Please try again.");
            } finally {
                  setIsSubmitting(false);
            }
      };

      if (!sessionData) return null;

      return (
            <div className="w-full">
                  <OnboardingHeader
                        title="Create Your Brand"
                        description="Tell us where to start and we'll prepare your initial profile."
                  />

                  <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                        <div className="flex flex-col gap-1.5">
                              <Label htmlFor="name" variant="brand">
                                    Brand Name
                              </Label>
                              <Input
                                    id="name"
                                    placeholder="Acme Corp"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    variant="brand"
                                    disabled={isSubmitting}
                                    required
                              />
                        </div>

                        <div className="flex flex-col gap-1.5">
                              <Label htmlFor="website" variant="brand">
                                    Website
                              </Label>
                              <div className="relative">
                                    <Input
                                          id="website"
                                          type="text"
                                          placeholder="yourbrand.com"
                                          value={website}
                                          onChange={(e) => setWebsite(e.target.value)}
                                          variant="brand"
                                          className="pr-10"
                                          disabled={isSubmitting}
                                          required
                                    />
                                    {domainValidationStatus === "validating" && (
                                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                                <Spinner className="size-4 text-brand-ink/30" />
                                          </div>
                                    )}
                                    {domainValidationStatus === "valid" && (
                                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                                <div className="flex size-5 items-center justify-center rounded-full bg-brand-accent">
                                                      <Check className="size-3 text-brand-ink" strokeWidth={2.5} />
                                                </div>
                                          </div>
                                    )}
                                    {domainValidationStatus === "invalid" && (
                                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                                <div className="flex size-5 items-center justify-center rounded-full bg-destructive">
                                                      <X className="size-3 text-white" />
                                                </div>
                                          </div>
                                    )}
                              </div>
                              {domainValidationStatus === "validating" && (
                                    <p className={fieldMessageVariants()}>Checking website reachability...</p>
                              )}
                              {domainValidationStatus === "valid" && (
                                    <p className={fieldMessageVariants({ tone: "success" })}>Website looks good.</p>
                              )}
                              {domainValidationStatus === "invalid" && (
                                    <p className="text-xs text-destructive">
                                          We couldn't verify this domain. Please check for typos and try again.
                                    </p>
                              )}
                        </div>

                        <LoadingButton
                              type="submit"
                              variant="brand"
                              className="mt-3 w-full"
                              loading={isSubmitting}
                              loadingText="Creating brand..."
                              disabled={
                                    isSubmitting ||
                                    !name.trim() ||
                                    !website.trim() ||
                                    domainValidationStatus !== "valid"
                              }
                        >
                              Continue
                        </LoadingButton>
                  </form>
            </div>
      );
}
